"use client";

import { useEffect, useState, useMemo, useRef } from "react";
import { useRouter } from "next/navigation";
import { fetchWithAuth } from "@/lib/auth";
import { DualScrollWrapper } from "@/components/directory/DualScrollWrapper";
import { WebsiteSyncPanel } from "./WebsiteSyncPanel";
import { TourButton } from "@/components/tours/TourButton";
import { useAutoTour } from "@/components/tours/useAutoTour";
import { TOURS } from "@/components/tours/catalog";

interface Assignment {
    assignment_id: string;
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
    profile_photo_url?: string;
    member_status?: string;
    role: string;
    entity_type: string;
    entity_id: number;
    entity_name: string;
    assignment_status: string;
}

interface GroupMeta {
    id: number;
    type: string;
    name: string;
    description?: string;
    status: string;
    volunteer_count: number;
    db_member_count?: number;
    region_code?: string;
    parent_id?: number | null;
    class_for_accounting?: string;
    code_for_accounting?: string;
}

const ROLES = ["Chair", "Vice Chair", "Staff Liaison", "Board Liaison", "Volunteer"];
const ENTITY_TYPES = ["committee", "chapter", "community"];
const STATUSES = ["active", "partially active", "proposed", "inactive"];
const MEMBER_STATUSES = ["active", "inactive"];

const COMMITTEE_ORDER: string[] = [
    "Board of Directors",
    "Audit Committee",
    "Executive Committee",
    "Finance Committee",
    "Governance Committee",
    "Admissions Committee",
    "Curriculum & Testing Committee",
    "Ethics & Standards Committee",
    "Charles H. Dow Award Committee",
    "Journal of Technical Analysis Committee",
    "Academia Outreach Committee",
    "Academic Outreach",
    "Industry Outreach Committee",
    "Industry Outreach",
    "The CMT Experience",
    "Thought Leadership",
    "Outreach and Advocacy",
    "The Member Experience",
    "Events (Summit/Symposium) Committee",
    "Global Chapter Development Committee",
    "Global Black Caucus",
    "Global Digital Network",
    "Women in Technical Analysis",
    "AMER Chapter Development Committee",
    "APAC Chapter Development Committee",
    "EMEA Chapter Development Committee",
    "LATAM Chapter Development Committee",
];

function hierarchySort(a: GroupedAssignment, b: GroupedAssignment): number {
    const typeOrder: Record<string, number> = { committee: 0, chapter: 1, community: 2 };
    const tA = typeOrder[a.entity_type] ?? 3;
    const tB = typeOrder[b.entity_type] ?? 3;
    if (tA !== tB) return tA - tB;
    if (a.entity_type === "committee") {
        const iA = COMMITTEE_ORDER.indexOf(a.entity_name);
        const iB = COMMITTEE_ORDER.indexOf(b.entity_name);
        if (iA !== -1 && iB !== -1) return iA - iB;
        if (iA !== -1) return -1;
        if (iB !== -1) return 1;
        return a.entity_name.localeCompare(b.entity_name);
    }
    const vA = a.db_member_count ?? a.volunteer_count ?? 0;
    const vB = b.db_member_count ?? b.volunteer_count ?? 0;
    if (vB !== vA) return vB - vA;
    return a.entity_name.localeCompare(b.entity_name);
}

const TYPE_BADGE: Record<string, string> = {
    committee: "bg-[#003d45] text-white",
    chapter: "bg-[#007a8a] text-white",
    community: "bg-[#ae232d] text-white",
};

const STATUS_STYLE: Record<string, string> = {
    "active":           "bg-emerald-50 text-emerald-700 border-emerald-200",
    "partially active": "bg-amber-50 text-amber-700 border-amber-200",
    "proposed":         "bg-blue-50 text-blue-700 border-blue-200",
    "inactive":         "bg-slate-100 text-slate-500 border-slate-200",
};

interface GroupedAssignment {
    entity_key: string;
    entity_type: string;
    entity_id: number;
    entity_name: string;
    entity_status: string;
    volunteer_count: number;
    db_member_count: number;
    roles: Record<string, Assignment[]>;
    all_assignments: Assignment[];
    class_for_accounting?: string;
    code_for_accounting?: string;
    parent_id?: number | null;
}

function isInactive(person: { member_status?: string }): boolean {
    return (person.member_status || "").toLowerCase() === "inactive";
}

function Avatar({ person }: { person: Assignment }) {
    const inactive = isInactive(person);
    return person.profile_photo_url ? (
        <img
            src={person.profile_photo_url}
            alt={`${person.first_name} ${person.last_name}`}
            title={inactive ? "Inactive member" : undefined}
            className={`w-6 h-6 rounded-full object-cover flex-shrink-0 ${inactive ? "grayscale opacity-50" : ""}`}
        />
    ) : (
        <div
            title={inactive ? "Inactive member" : undefined}
            className={`w-6 h-6 rounded-full text-white text-[8px] font-black flex items-center justify-center uppercase flex-shrink-0 ${inactive ? "bg-[#b0b0b0] opacity-70" : "bg-[#003d45]"}`}
        >
            {(person.first_name?.[0] || "")}{(person.last_name?.[0] || "")}
        </div>
    );
}

function AvatarStack({ people, onRemove }: { people: Assignment[], onRemove?: (id: string) => void }) {
    const [expanded, setExpanded] = useState(false);
    if (!people || people.length === 0) return <span className="text-[11px] text-[#aaa] font-medium">-</span>;

    const maxInitial = 2;
    const hasMore = people.length > maxInitial;
    const displayed = expanded || !hasMore ? people : people.slice(0, maxInitial);

    return (
        <div className="flex flex-col gap-1.5 min-w-0 w-full">
            {displayed.map(p => {
                const inactive = isInactive(p);
                return (
                <div key={p.assignment_id} className="flex items-center gap-1.5 group min-w-0 w-full">
                    <Avatar person={p} />
                    <div className="flex flex-col min-w-0 flex-1">
                        <span 
                            title={`${p.first_name} ${p.last_name}${inactive ? " (Inactive)" : ""}`}
                            className={`text-xs font-semibold truncate leading-tight block w-full ${inactive ? "text-[#999]" : "text-[#1a1a1a]"}`}
                        >
                            {p.first_name} {p.last_name}
                            {inactive && <span className="ml-1 text-[8px] font-black uppercase tracking-wider text-[#aaa]">(Inact.)</span>}
                        </span>
                    </div>
                    {onRemove && (
                        <button 
                            onClick={() => onRemove(p.assignment_id)} 
                            className="opacity-0 group-hover:opacity-100 text-[#ae232d] hover:bg-[#ae232d]/10 rounded p-0.5 transition-all flex-shrink-0" 
                            title="Remove role"
                        >
                            <span className="material-symbols-outlined text-[13px]">remove</span>
                        </button>
                    )}
                </div>
                );
            })}
            {hasMore && (
                <button
                    onClick={() => setExpanded(prev => !prev)}
                    className="text-[10px] font-bold text-[#007a8a] hover:text-[#003d45] flex items-center gap-0.5 mt-0.5 self-start px-1.5 py-0.5 rounded bg-[#e8f4f5] hover:bg-[#d5ecee] transition-colors"
                >
                    <span>{expanded ? "Show less" : `+${people.length - maxInitial} more`}</span>
                    <span className="material-symbols-outlined text-[12px]">
                        {expanded ? "expand_less" : "expand_more"}
                    </span>
                </button>
            )}
        </div>
    );
}

// ── Multi-Select Dropdown ─────────────────────────────────────────────────────

function MultiSelectDropdown({
    label, options, selected, onToggle, onClear, badgeMap,
}: {
    label: string;
    options: string[];
    selected: Set<string>;
    onToggle: (val: string) => void;
    onClear: () => void;
    badgeMap?: Record<string, string>;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handler(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const count = selected.size;
    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(p => !p)}
                className={`flex items-center gap-2 px-3 py-2.5 border rounded-xl text-sm bg-white focus:outline-none transition-colors whitespace-nowrap ${count > 0 ? "border-[#003d45] text-[#003d45] font-bold" : "border-[#e2e8f0] text-[#555] hover:border-[#bbb]"}`}
            >
                {label}
                {count > 0 && (
                    <span className="bg-[#003d45] text-white text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none">{count}</span>
                )}
                <span className="material-symbols-outlined text-[16px] text-[#aaa]">{open ? "expand_less" : "expand_more"}</span>
            </button>

            {open && (
                <div className="absolute top-full left-0 mt-1.5 bg-white border border-[#e2e8f0] rounded-xl shadow-xl z-30 min-w-[180px] py-1.5 overflow-hidden">
                    {count > 0 && (
                        <button
                            onClick={() => { onClear(); setOpen(false); }}
                            className="w-full text-left px-3 py-2 text-[11px] text-[#ae232d] font-bold hover:bg-red-50 transition-colors border-b border-[#f0f0f0] flex items-center gap-1.5"
                        >
                            <span className="material-symbols-outlined text-[13px]">close</span>
                            Clear selection
                        </button>
                    )}
                    {options.map(opt => (
                        <label key={opt} className="flex items-center gap-2.5 px-3 py-2 hover:bg-[#f4f8f8] cursor-pointer transition-colors">
                            <input
                                type="checkbox"
                                checked={selected.has(opt)}
                                onChange={() => onToggle(opt)}
                                className="accent-[#003d45] w-3.5 h-3.5 flex-shrink-0"
                            />
                            {badgeMap?.[opt] ? (
                                <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full ${badgeMap[opt]}`}>{opt}</span>
                            ) : (
                                <span className="text-sm text-[#333] capitalize">{opt}</span>
                            )}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Active Filter Chips ───────────────────────────────────────────────────────

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
    return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#003d45]/10 text-[#003d45] text-[11px] font-bold rounded-full border border-[#003d45]/20">
            {label}
            <button onClick={onRemove} className="hover:text-[#ae232d] transition-colors ml-0.5">
                <span className="material-symbols-outlined text-[12px]">close</span>
            </button>
        </span>
    );
}

// ── Sponsor Chapter Dropdown ──────────────────────────────────────────────────

function SponsorDropdown({ chapters, selected, onToggle, onClear }: {
    chapters: GroupMeta[];
    selected: Set<number>;
    onToggle: (id: number) => void;
    onClear: () => void;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handler(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const count = selected.size;
    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(p => !p)}
                className={`flex items-center gap-2 px-3 py-2.5 border rounded-xl text-sm bg-white focus:outline-none transition-colors whitespace-nowrap ${count > 0 ? "border-[#003d45] text-[#003d45] font-bold" : "border-[#e2e8f0] text-[#555] hover:border-[#bbb]"}`}
            >
                Sponsoring Chapter
                {count > 0 && (
                    <span className="bg-[#003d45] text-white text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none">{count}</span>
                )}
                <span className="material-symbols-outlined text-[16px] text-[#aaa]">{open ? "expand_less" : "expand_more"}</span>
            </button>
            {open && (
                <div className="absolute top-full left-0 mt-1.5 bg-white border border-[#e2e8f0] rounded-xl shadow-xl z-30 min-w-[220px] py-1.5 overflow-hidden max-h-72 overflow-y-auto">
                    {count > 0 && (
                        <button
                            onClick={() => { onClear(); setOpen(false); }}
                            className="w-full text-left px-3 py-2 text-[11px] text-[#ae232d] font-bold hover:bg-red-50 transition-colors border-b border-[#f0f0f0] flex items-center gap-1.5 sticky top-0 bg-white"
                        >
                            <span className="material-symbols-outlined text-[13px]">close</span>
                            Clear selection
                        </button>
                    )}
                    {chapters.map(ch => (
                        <label key={ch.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-[#f4f8f8] cursor-pointer transition-colors">
                            <input
                                type="checkbox"
                                checked={selected.has(ch.id)}
                                onChange={() => onToggle(ch.id)}
                                className="accent-[#003d45] w-3.5 h-3.5 flex-shrink-0"
                            />
                            <span className="text-sm text-[#333]">{ch.name}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

export function AssignmentsPanel() {
    const router = useRouter();
    const [showSync, setShowSync] = useState(false);
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [groups, setGroups] = useState<GroupMeta[]>([]);
    const [loading, setLoading] = useState(true);
    const [mapMode, setMapMode] = useState<"assignments_only" | "all_active">("assignments_only");
    const [mapModeLoading, setMapModeLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [filterTypes, setFilterTypes] = useState<Set<string>>(new Set());
    const [filterRoles, setFilterRoles] = useState<Set<string>>(new Set());
    const [filterStatuses, setFilterStatuses] = useState<Set<string>>(new Set());
    const [filterMemberStatus, setFilterMemberStatus] = useState<Set<string>>(new Set());
    const [filterAssignments, setFilterAssignments] = useState<"all" | "with" | "without">("all");
    const [filterSponsor, setFilterSponsor] = useState<Set<number>>(new Set());
    const [sortField, setSortField] = useState<"name" | "entity">("entity");
    const [sortAsc, setSortAsc] = useState(true);

    // Add assignment modal
    const [showAddModal, setShowAddModal] = useState(false);
    const [userSearch, setUserSearch] = useState("");
    const [userResults, setUserResults] = useState<any[]>([]);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [selectedGroup, setSelectedGroup] = useState("");
    const [selectedRole, setSelectedRole] = useState(ROLES[4]);
    const [submitting, setSubmitting] = useState(false);
    const [linkedinInput, setLinkedinInput] = useState("");
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const photoFileRef = useRef<HTMLInputElement>(null);

    const [togglingStatus, setTogglingStatus] = useState<Set<string>>(new Set());
    const { replay: replayTour } = useAutoTour("staff.assignments", TOURS["staff.assignments"]);

    useEffect(() => { loadAll(); }, []);

    function loadAll() {
        setLoading(true);
        Promise.all([
            fetchWithAuth("/api/v1/organizations/assignments?limit=2000").then(r => r.ok ? r.json() : []),
            fetchWithAuth("/api/v1/organizations/all-groups?include_inactive=true").then(r => r.ok ? r.json() : []),
            fetch("/api/v1/settings/map_display_mode").then(r => r.ok ? r.json() : null),
        ]).then(([assignData, groupData, settingData]) => {
            setAssignments(assignData);
            setGroups(Array.isArray(groupData) ? groupData : []);
            if (settingData?.value) setMapMode(settingData.value === "all_active" ? "all_active" : "assignments_only");
        }).finally(() => setLoading(false));
    }

    async function handleMapModeChange(mode: "assignments_only" | "all_active") {
        setMapModeLoading(true);
        const res = await fetchWithAuth("/api/v1/settings/map_display_mode", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value: mode }),
        });
        if (res.ok) setMapMode(mode);
        setMapModeLoading(false);
    }

    function loadAssignments() {
        fetchWithAuth("/api/v1/organizations/assignments?limit=2000")
            .then(r => r.ok ? r.json() : [])
            .then(d => setAssignments(d));
    }

    useEffect(() => {
        if (userSearch.length < 2) { setUserResults([]); return; }
        const timer = setTimeout(() => {
            fetchWithAuth(`/api/v1/organizations/users/search?q=${encodeURIComponent(userSearch)}`)
                .then(r => r.ok ? r.json() : [])
                .then(d => setUserResults(d));
        }, 300);
        return () => clearTimeout(timer);
    }, [userSearch]);

    function toggleFilter(set: Set<string>, val: string): Set<string> {
        const next = new Set(set);
        if (next.has(val)) next.delete(val); else next.add(val);
        return next;
    }

    const chapterNameById = useMemo(() => {
        const map: Record<number, string> = {};
        for (const g of groups) {
            if (g.type === "chapter") map[g.id] = g.name;
        }
        return map;
    }, [groups]);

    const chaptersWithCommunities = useMemo(() => {
        const parentIds = new Set(groups.filter(g => g.type === "community" && g.parent_id).map(g => g.parent_id!));
        return groups.filter(g => g.type === "chapter" && parentIds.has(g.id)).sort((a, b) => a.name.localeCompare(b.name));
    }, [groups]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();

        const groupedMap = new Map<string, GroupedAssignment>();
        for (const g of groups) {
            const key = `${g.type}-${g.id}`;
            groupedMap.set(key, {
                entity_key: key,
                entity_type: g.type,
                entity_id: g.id,
                entity_name: g.name || "",
                entity_status: g.status || "active",
                volunteer_count: g.volunteer_count || 0,
                db_member_count: g.db_member_count ?? 0,
                roles: {},
                all_assignments: [],
                class_for_accounting: g.class_for_accounting || "",
                code_for_accounting: g.code_for_accounting || "",
                parent_id: g.parent_id ?? null,
            });
        }

        for (const a of assignments) {
            if (!a.first_name?.trim() && !a.last_name?.trim()) continue;
            if (filterMemberStatus.size > 0) {
                const ms = (a.member_status || "active").toLowerCase();
                if (!filterMemberStatus.has(ms)) continue;
            }
            const key = `${a.entity_type}-${a.entity_id}`;
            if (!groupedMap.has(key)) {
                groupedMap.set(key, {
                    entity_key: key,
                    entity_type: a.entity_type,
                    entity_id: a.entity_id,
                    entity_name: a.entity_name,
                    entity_status: "active",
                    volunteer_count: 0,
                    db_member_count: 0,
                    roles: {},
                    all_assignments: [],
                });
            }
            const group = groupedMap.get(key)!;
            if (!group.entity_name) group.entity_name = a.entity_name;
            group.all_assignments.push(a);
            let mappedRole = a.role;
            if (!["Chair", "Vice Chair", "Board Liaison", "Staff Liaison"].includes(mappedRole)) mappedRole = "Volunteers";
            if (!group.roles[mappedRole]) group.roles[mappedRole] = [];
            group.roles[mappedRole].push(a);
        }

        return Array.from(groupedMap.values())
            .filter(g => {
                if (filterTypes.size > 0 && !filterTypes.has(g.entity_type)) return false;
                if (filterStatuses.size > 0) {
                    const s = (g.entity_status || "active").toLowerCase();
                    if (!filterStatuses.has(s)) return false;
                }
                if (filterAssignments !== "all") {
                    const hasAny = g.all_assignments.length > 0;
                    if (filterAssignments === "with" && !hasAny) return false;
                    if (filterAssignments === "without" && hasAny) return false;
                }
                if (filterSponsor.size > 0) {
                    if (g.entity_type !== "community") return false;
                    if (!g.parent_id || !filterSponsor.has(g.parent_id)) return false;
                }
                if (filterRoles.size > 0) {
                    const hasRole = Array.from(filterRoles).some(role => {
                        const roleKey = ["Chair", "Vice Chair", "Board Liaison", "Staff Liaison"].includes(role) ? role : "Volunteers";
                        return (g.roles[roleKey]?.length || 0) > 0;
                    });
                    if (!hasRole) return false;
                }
                if (q) {
                    const nameMatch = g.entity_name.toLowerCase().includes(q);
                    const typeMatch = g.entity_type.toLowerCase().includes(q);
                    const peopleMatch = g.all_assignments.some(a =>
                        `${a.first_name} ${a.last_name} ${a.role}`.toLowerCase().includes(q)
                    );
                    if (!nameMatch && !typeMatch && !peopleMatch) return false;
                }
                return true;
            })
            .sort((a, b) => sortAsc ? hierarchySort(a, b) : hierarchySort(b, a));
    }, [assignments, groups, search, filterTypes, filterRoles, filterStatuses, filterMemberStatus, filterAssignments, filterSponsor, sortAsc]);

    function openAddModalForGroup(entity_type: string, entity_id: number) {
        setSelectedGroup(`${entity_type}-${entity_id}`);
        setUserSearch(""); setSelectedUser(null); setSelectedRole(ROLES[4]);
        setLinkedinInput(""); setPhotoFile(null); setPhotoPreview("");
        setShowAddModal(true);
    }

    async function handleDelete(assignment_id: string) {
        if (!confirm("Are you sure you want to remove this assignment?")) return;
        const res = await fetchWithAuth(`/api/v1/organizations/assignments/${assignment_id}`, { method: "DELETE" });
        if (res.ok) setAssignments(prev => prev.filter(a => a.assignment_id !== assignment_id));
        else alert("Failed to delete assignment. Are you an admin?");
    }

    async function handleAdd() {
        if (!selectedUser || !selectedGroup || !selectedRole) return;
        setSubmitting(true);
        const [entity_type, entity_id] = selectedGroup.split("-");
        const linkedinUrl = linkedinInput.trim();
        if (linkedinUrl) {
            await fetchWithAuth(`/api/v1/organizations/users/${selectedUser.id}`, {
                method: "PATCH", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ linkedin: linkedinUrl }),
            });
        }
        if (photoFile) {
            const form = new FormData(); form.append("file", photoFile);
            await fetchWithAuth(`/api/v1/organizations/users/${selectedUser.id}/photo`, { method: "POST", body: form });
        }
        const res = await fetchWithAuth("/api/v1/organizations/assignments", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id: selectedUser.id, entity_type, entity_id: parseInt(entity_id, 10), role: selectedRole, status: "active" }),
        });
        if (res.ok) {
            // Fire-and-forget bio generation if a LinkedIn URL was provided and user has no bio yet
            if (linkedinUrl && !selectedUser.bio) {
                const userId = selectedUser.id;
                const fullName = `${selectedUser.first_name} ${selectedUser.last_name}`.trim();
                fetchWithAuth("/api/v1/presenters/generate", {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name: fullName, linkedin: linkedinUrl }),
                }).then(r => r.ok ? r.json() : null).then(data => {
                    if (data?.bio) {
                        fetchWithAuth(`/api/v1/organizations/users/${userId}`, {
                            method: "PATCH", headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ bio: data.bio }),
                        });
                    }
                }).catch(() => {});
            }
            loadAssignments();
            setShowAddModal(false); setSelectedUser(null); setUserSearch(""); setSelectedGroup(""); setSelectedRole(ROLES[4]);
            setLinkedinInput(""); setPhotoFile(null); setPhotoPreview("");
        } else alert("Failed to add assignment. Are you an admin?");
        setSubmitting(false);
    }

    async function handleSetStatus(g: GroupedAssignment, newStatus: string) {
        setTogglingStatus(prev => new Set(prev).add(g.entity_key));
        const res = await fetchWithAuth(`/api/v1/organizations/${g.entity_type}s/${g.entity_id}`, {
            method: "PUT", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: newStatus }),
        });
        if (res.ok) {
            setGroups(prev => prev.map(grp =>
                grp.id === g.entity_id && grp.type === g.entity_type ? { ...grp, status: newStatus } : grp
            ));
        } else alert("Failed to update status. Are you an admin?");
        setTogglingStatus(prev => { const s = new Set(prev); s.delete(g.entity_key); return s; });
    }

    function clearAllFilters() {
        setSearch(""); setFilterTypes(new Set()); setFilterRoles(new Set());
        setFilterStatuses(new Set()); setFilterMemberStatus(new Set()); setFilterAssignments("all"); setFilterSponsor(new Set());
    }

    const hasActiveFilters = search || filterTypes.size > 0 || filterRoles.size > 0 || filterStatuses.size > 0 || filterMemberStatus.size > 0 || filterAssignments !== "all" || filterSponsor.size > 0;

    const TYPE_BADGE_MAP: Record<string, string> = {
        committee: "bg-[#003d45] text-white",
        chapter: "bg-[#007a8a] text-white",
        community: "bg-[#ae232d] text-white",
    };

    if (showSync) {
        return <WebsiteSyncPanel onBack={() => setShowSync(false)} />;
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div>
                    <h2 className="text-xl sm:text-2xl font-black text-[#003d45] mb-1">Group Assignments</h2>
                    <p className="text-sm text-[#666666]">{filtered.length} of {groups.length} groups shown · {assignments.length} total assignments</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <TourButton onClick={replayTour} />
                    <button
                        onClick={() => setShowSync(true)}
                        className="flex items-center gap-2 border border-[#e2e8f0] bg-white hover:border-[#003d45] hover:text-[#003d45] text-[#555] px-4 py-2 rounded-xl text-sm font-bold transition-colors"
                    >
                        <span className="material-symbols-outlined text-[18px]">sync_alt</span>
                        Website Sync
                    </button>
                    <button
                        id="assign-add"
                        onClick={() => { setSelectedUser(null); setUserSearch(""); setSelectedGroup(""); setSelectedRole(ROLES[4]); setLinkedinInput(""); setPhotoFile(null); setPhotoPreview(""); setShowAddModal(true); }}
                        className="bg-[#003d45] hover:bg-[#007a8a] text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-colors"
                    >
                        <span className="material-symbols-outlined text-[18px]">add</span>
                        Add Assignment
                    </button>
                </div>
            </div>

            {/* BAP Toggle — controls what the public map displays */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 bg-[#f7faf9] border border-[#dce8e6] rounded-xl">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <span className="material-symbols-outlined text-[20px] text-[#007a8a] flex-shrink-0">public</span>
                    <div className="min-w-0">
                        <div className="text-sm font-bold text-[#003d45]">Public Map Display</div>
                        <div className="text-xs text-[#666]">Controls what is shown on the public-facing groups map</div>
                    </div>
                </div>
                <div className={`flex items-center bg-white border border-[#e2e8f0] rounded-xl overflow-hidden text-xs font-bold w-full sm:w-auto flex-shrink-0 ${mapModeLoading ? "opacity-60 pointer-events-none" : ""}`}>
                    <button
                        onClick={() => handleMapModeChange("assignments_only")}
                        className={`flex-1 sm:flex-none px-3 py-2 transition-colors whitespace-nowrap ${mapMode === "assignments_only" ? "bg-[#003d45] text-white" : "text-[#666] hover:bg-[#f0f0f0]"}`}
                    >
                        Groups with Assignments
                    </button>
                    <button
                        onClick={() => handleMapModeChange("all_active")}
                        className={`flex-1 sm:flex-none px-3 py-2 transition-colors whitespace-nowrap ${mapMode === "all_active" ? "bg-[#003d45] text-white" : "text-[#666] hover:bg-[#f0f0f0]"}`}
                    >
                        All Active Groups
                    </button>
                </div>
                {mapModeLoading && <span className="material-symbols-outlined text-[16px] text-[#007a8a] animate-spin">progress_activity</span>}
            </div>

            {/* Filters row */}
            <div id="assign-filters" className="space-y-3">
                <div className="flex flex-wrap gap-2.5 items-center">
                    <div className="relative flex-1 min-w-[200px] max-w-xs">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#999] text-base">search</span>
                        <input type="text" placeholder="Search name, role, group…" value={search} onChange={e => setSearch(e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a] bg-white" />
                    </div>

                    <MultiSelectDropdown
                        label="Type"
                        options={ENTITY_TYPES}
                        selected={filterTypes}
                        onToggle={val => setFilterTypes(toggleFilter(filterTypes, val))}
                        onClear={() => setFilterTypes(new Set())}
                        badgeMap={TYPE_BADGE_MAP}
                    />

                    <MultiSelectDropdown
                        label="Role"
                        options={ROLES}
                        selected={filterRoles}
                        onToggle={val => setFilterRoles(toggleFilter(filterRoles, val))}
                        onClear={() => setFilterRoles(new Set())}
                    />

                    <MultiSelectDropdown
                        label="Status"
                        options={STATUSES}
                        selected={filterStatuses}
                        onToggle={val => setFilterStatuses(toggleFilter(filterStatuses, val))}
                        onClear={() => setFilterStatuses(new Set())}
                    />

                    <MultiSelectDropdown
                        label="Member Status"
                        options={MEMBER_STATUSES}
                        selected={filterMemberStatus}
                        onToggle={val => setFilterMemberStatus(toggleFilter(filterMemberStatus, val))}
                        onClear={() => setFilterMemberStatus(new Set())}
                    />

                    {/* Sponsoring Chapter filter */}
                    <SponsorDropdown
                        chapters={chaptersWithCommunities}
                        selected={filterSponsor}
                        onToggle={id => {
                            const next = new Set(filterSponsor);
                            if (next.has(id)) next.delete(id); else next.add(id);
                            setFilterSponsor(next);
                        }}
                        onClear={() => setFilterSponsor(new Set())}
                    />

                    {/* Has assignments pill toggle */}
                    <div className="flex items-center bg-white border border-[#e2e8f0] rounded-xl overflow-hidden text-xs font-bold">
                        {([["all", "All"], ["with", "Has Assignments"], ["without", "No Assignments"]] as const).map(([val, label]) => (
                            <button key={val} onClick={() => setFilterAssignments(val)}
                                className={`px-3 py-2.5 transition-colors ${filterAssignments === val ? "bg-[#003d45] text-white" : "text-[#666] hover:bg-[#f0f0f0]"}`}>
                                {label}
                            </button>
                        ))}
                    </div>

                    {hasActiveFilters && (
                        <button onClick={clearAllFilters} className="text-xs text-[#ae232d] font-bold hover:underline flex items-center gap-1">
                            <span className="material-symbols-outlined text-[13px]">close</span>
                            Clear all
                        </button>
                    )}
                </div>

                {/* Active filter chips */}
                {hasActiveFilters && (
                    <div className="flex flex-wrap gap-2 items-center">
                        {search && <FilterChip label={`"${search}"`} onRemove={() => setSearch("")} />}
                        {Array.from(filterTypes).map(t => (
                            <FilterChip key={t} label={t.charAt(0).toUpperCase() + t.slice(1)} onRemove={() => setFilterTypes(toggleFilter(filterTypes, t))} />
                        ))}
                        {Array.from(filterRoles).map(r => (
                            <FilterChip key={r} label={r} onRemove={() => setFilterRoles(toggleFilter(filterRoles, r))} />
                        ))}
                        {Array.from(filterStatuses).map(s => (
                            <FilterChip key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} onRemove={() => setFilterStatuses(toggleFilter(filterStatuses, s))} />
                        ))}
                        {Array.from(filterMemberStatus).map(s => (
                            <FilterChip key={s} label={`Member: ${s.charAt(0).toUpperCase() + s.slice(1)}`} onRemove={() => setFilterMemberStatus(toggleFilter(filterMemberStatus, s))} />
                        ))}
                        {filterAssignments !== "all" && (
                            <FilterChip label={filterAssignments === "with" ? "Has Assignments" : "No Assignments"} onRemove={() => setFilterAssignments("all")} />
                        )}
                        {Array.from(filterSponsor).map(id => (
                            <FilterChip key={id} label={`Sponsor: ${chapterNameById[id] || id}`} onRemove={() => {
                                const next = new Set(filterSponsor); next.delete(id); setFilterSponsor(next);
                            }} />
                        ))}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-40">
                    <div className="w-7 h-7 border-2 border-[#e2e8f0] border-t-[#003d45] rounded-full animate-spin" />
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden flex flex-col w-full">
                    <DualScrollWrapper>
                        <table className="w-full text-sm border-collapse table-fixed min-w-[700px] lg:min-w-0">
                            <thead className="sticky top-0 z-10 bg-[#003d45] text-white shadow-sm">
                                <tr className="bg-[#003d45] text-white">
                                    <th 
                                        className="text-left px-3 sm:px-4 py-3 text-[10px] font-black uppercase tracking-wider cursor-pointer hover:bg-[#002d33] transition-colors w-[22%] min-w-0" 
                                        onClick={() => setSortAsc(p => !p)}
                                    >
                                        <div className="flex items-center gap-1 truncate">
                                            Group
                                            <span className="material-symbols-outlined text-[13px]">{sortAsc ? "arrow_upward" : "arrow_downward"}</span>
                                        </div>
                                    </th>
                                    <th className="text-left px-2 sm:px-3 py-3 text-[10px] font-black uppercase tracking-wider w-[13%] min-w-0 truncate">Chair</th>
                                    <th className="text-left px-2 sm:px-3 py-3 text-[10px] font-black uppercase tracking-wider w-[13%] min-w-0 truncate">Vice Chair</th>
                                    <th className="text-left px-2 sm:px-3 py-3 text-[10px] font-black uppercase tracking-wider w-[18%] min-w-0 truncate">Volunteers</th>
                                    <th className="text-left px-2 sm:px-3 py-3 text-[10px] font-black uppercase tracking-wider w-[13%] min-w-0 truncate">Board Liaison</th>
                                    <th className="text-left px-2 sm:px-3 py-3 text-[10px] font-black uppercase tracking-wider w-[13%] min-w-0 truncate">Staff Liaison</th>
                                    <th className="text-right px-2 py-3 text-[10px] font-black uppercase tracking-wider w-[8%] min-w-0"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr><td colSpan={7} className="px-5 py-12 text-center text-[#999] text-sm">No assignments match your filters.</td></tr>
                                ) : filtered.map((g, i) => {
                                    const statusLower = (g.entity_status || "active").toLowerCase();
                                    const isToggling = togglingStatus.has(g.entity_key);
                                    return (
                                        <tr key={g.entity_key} className={`hover:bg-[#f9fafa] transition-colors ${i % 2 === 0 ? "bg-white" : "bg-[#fcfcfc]"} ${statusLower === "inactive" ? "opacity-60" : ""}`}>
                                            <td className="px-3 sm:px-4 py-3 sm:py-3.5 border-t border-[#e2e8f0] align-top min-w-0 overflow-hidden">
                                                <div className="flex flex-col gap-1 items-start min-w-0 w-full">
                                                    <span className={`text-[8.5px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full ${TYPE_BADGE[g.entity_type] || "bg-slate-200 text-slate-600"}`}>
                                                        {g.entity_type}
                                                    </span>
                                                    <span title={g.entity_name} className="text-xs sm:text-sm font-bold text-[#003d45] leading-tight break-words line-clamp-2 block w-full">{g.entity_name}</span>
                                                    {g.entity_type === "community" && g.parent_id && chapterNameById[g.parent_id] && (
                                                        <span title={`Sponsoring Group: ${chapterNameById[g.parent_id]}`} className="text-[10px] text-[#666] leading-tight block truncate w-full">
                                                            Sponsor: <span className="font-semibold text-[#003d45]">{chapterNameById[g.parent_id]}</span>
                                                        </span>
                                                    )}
                                                    {g.class_for_accounting && (
                                                        <span className="text-[8.5px] font-mono text-[#007a8a] bg-[#e8f4f5] px-1.5 py-0.2 rounded tracking-wide inline-block">{g.class_for_accounting}</span>
                                                    )}
                                                    <span className="text-[9.5px] font-bold text-[#999] uppercase tracking-wider block truncate w-full">
                                                        {g.entity_type === "chapter"
                                                            ? `${g.db_member_count} active members`
                                                            : g.entity_type === "community"
                                                                ? g.db_member_count > 0
                                                                    ? `${g.db_member_count} active members`
                                                                    : "members not yet synced"
                                                                : `${g.all_assignments.length} active volunteers`}
                                                    </span>

                                                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                                        <div className="relative flex items-center">
                                                            {isToggling && (
                                                                <div className="absolute left-1.5 w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin text-[#666]" />
                                                            )}
                                                            <select
                                                                value={(g.entity_status || "active").toLowerCase()}
                                                                onChange={e => handleSetStatus(g, e.target.value)}
                                                                disabled={isToggling}
                                                                className={`pl-1.5 pr-4 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border appearance-none cursor-pointer focus:outline-none disabled:opacity-50 transition-colors ${
                                                                    STATUS_STYLE[(g.entity_status || "active").toLowerCase()] || "bg-slate-100 text-slate-500 border-slate-200"
                                                                }`}
                                                            >
                                                                <option value="active">Active</option>
                                                                <option value="partially active">Partially Active</option>
                                                                <option value="proposed">Proposed</option>
                                                                <option value="inactive">Inactive</option>
                                                            </select>
                                                        </div>
                                                        <button
                                                            onClick={() => router.push(`/dashboard/manage/groups/${g.entity_type}/${g.entity_id}/edit`)}
                                                            title="Edit group details"
                                                            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border border-[#e2e8f0] text-[#666] hover:bg-[#f0f0f0] transition-colors"
                                                        >
                                                            <span className="material-symbols-outlined text-[11px]">edit</span>
                                                            Edit
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-2 sm:px-3 py-3 sm:py-3.5 border-t border-[#e2e8f0] align-top min-w-0 overflow-hidden"><AvatarStack people={g.roles["Chair"]} onRemove={handleDelete} /></td>
                                            <td className="px-2 sm:px-3 py-3 sm:py-3.5 border-t border-[#e2e8f0] align-top min-w-0 overflow-hidden"><AvatarStack people={g.roles["Vice Chair"]} onRemove={handleDelete} /></td>
                                            <td className="px-2 sm:px-3 py-3 sm:py-3.5 border-t border-[#e2e8f0] align-top min-w-0 overflow-hidden"><AvatarStack people={g.roles["Volunteers"]} onRemove={handleDelete} /></td>
                                            <td className="px-2 sm:px-3 py-3 sm:py-3.5 border-t border-[#e2e8f0] align-top min-w-0 overflow-hidden"><AvatarStack people={g.roles["Board Liaison"]} onRemove={handleDelete} /></td>
                                            <td className="px-2 sm:px-3 py-3 sm:py-3.5 border-t border-[#e2e8f0] align-top min-w-0 overflow-hidden"><AvatarStack people={g.roles["Staff Liaison"]} onRemove={handleDelete} /></td>
                                            <td className="px-2 py-3 sm:py-3.5 border-t border-[#e2e8f0] align-top text-right min-w-0 overflow-hidden">
                                                <button onClick={() => openAddModalForGroup(g.entity_type, g.entity_id)}
                                                    className="text-[#007a8a] hover:bg-[#e8f4f5] px-1.5 sm:px-2 py-1 rounded-lg text-xs font-bold transition-colors inline-flex items-center gap-0.5">
                                                    <span className="material-symbols-outlined text-[14px]">add</span> Add
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </DualScrollWrapper>
                </div>
            )}

            {/* Add Assignment Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-[#003d45]/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl relative">
                        <button onClick={() => setShowAddModal(false)} className="absolute top-4 right-4 text-[#999] hover:text-[#333]">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                        <h3 className="text-xl font-black text-[#003d45] mb-5">Add Assignment</h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">User</label>
                                {selectedUser ? (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between bg-[#f0f2f4] p-3 rounded-xl border border-[#e2e8f0]">
                                            <div className="flex items-center gap-3">
                                                {photoPreview ? (
                                                    <img src={photoPreview} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                                                ) : selectedUser.profile_photo_url ? (
                                                    <img src={selectedUser.profile_photo_url} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                                                ) : (
                                                    <div className="w-9 h-9 rounded-full bg-[#003d45] text-white flex items-center justify-center text-xs font-black uppercase flex-shrink-0">
                                                        {selectedUser.first_name[0]}{selectedUser.last_name[0]}
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="text-sm font-bold text-[#1a1a1a]">{selectedUser.first_name} {selectedUser.last_name}</p>
                                                    <p className="text-xs text-[#666]">{selectedUser.email}</p>
                                                </div>
                                            </div>
                                            <button onClick={() => { setSelectedUser(null); setLinkedinInput(""); setPhotoFile(null); setPhotoPreview(""); }} className="text-[#999] hover:text-[#ae232d]">
                                                <span className="material-symbols-outlined text-xl">close</span>
                                            </button>
                                        </div>
                                        {(!selectedUser.linkedin || !selectedUser.profile_photo_url) && (
                                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-3">
                                                <p className="text-xs font-bold text-amber-700 flex items-center gap-1.5">
                                                    <span className="material-symbols-outlined text-[15px]">warning</span>
                                                    Missing profile info. You can add it below.
                                                </p>
                                                {!selectedUser.linkedin && (
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1">LinkedIn URL</label>
                                                        <input type="url" placeholder="https://linkedin.com/in/username" value={linkedinInput} onChange={e => setLinkedinInput(e.target.value)}
                                                            className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:outline-none focus:border-amber-500 bg-white" />
                                                    </div>
                                                )}
                                                {!selectedUser.profile_photo_url && (
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1">Profile Photo</label>
                                                        <div className="flex items-center gap-3">
                                                            {photoPreview && <img src={photoPreview} className="w-10 h-10 rounded-full object-cover border border-amber-200" />}
                                                            <button type="button" onClick={() => photoFileRef.current?.click()}
                                                                className="px-3 py-1.5 border border-amber-300 rounded-lg text-xs font-bold text-amber-700 hover:bg-amber-100 transition-colors">
                                                                {photoFile ? "Change Photo" : "Upload Photo"}
                                                            </button>
                                                            <input ref={photoFileRef} type="file" accept="image/*" className="hidden"
                                                                onChange={e => { const f = e.target.files?.[0]; if (f) { setPhotoFile(f); setPhotoPreview(URL.createObjectURL(f)); } }} />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#999] text-base pointer-events-none">search</span>
                                        <input type="text" placeholder="Search by name or email..." value={userSearch} onChange={e => setUserSearch(e.target.value)}
                                            className="w-full pl-9 pr-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm focus:outline-none focus:border-[#007a8a]" />
                                        {userResults.length > 0 && (
                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#e2e8f0] rounded-xl shadow-lg z-10 max-h-64 overflow-y-auto">
                                                {userResults.map(u => (
                                                    <button key={u.id} onClick={() => { setSelectedUser(u); setUserResults([]); setUserSearch(""); }}
                                                        className="w-full text-left px-3 py-2.5 hover:bg-[#f0f2f4] border-b border-[#f0f0f0] last:border-0 flex items-center gap-3">
                                                        {u.profile_photo_url ? (
                                                            <img src={u.profile_photo_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                                                        ) : (
                                                            <div className="w-8 h-8 rounded-full bg-[#003d45] text-white flex items-center justify-center text-[10px] font-black uppercase flex-shrink-0">
                                                                {(u.first_name?.[0] || "")}{(u.last_name?.[0] || "")}
                                                            </div>
                                                        )}
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-sm font-bold text-[#1a1a1a]">{u.first_name} {u.last_name}</span>
                                                                {u.member_status && (
                                                                    <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full flex-shrink-0 ${u.member_status.toLowerCase() === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                                                                        {u.member_status}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-[11px] text-[#666] truncate">{u.email}</p>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {userSearch.length >= 2 && userResults.length === 0 && (
                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#e2e8f0] rounded-xl shadow-lg z-10 px-4 py-3 text-sm text-[#999]">
                                                No users found
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">Group</label>
                                <select value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)}
                                    className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm bg-white focus:outline-none focus:border-[#007a8a]">
                                    <option value="" disabled>Select a group...</option>
                                    {groups.map(g => (
                                        <option key={`${g.type}-${g.id}`} value={`${g.type}-${g.id}`}>
                                            {g.name} ({g.type}){g.status && g.status !== "active" ? ` — ${g.status}` : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-[#666] uppercase tracking-wider mb-1">Role</label>
                                <select value={selectedRole} onChange={e => setSelectedRole(e.target.value)}
                                    className="w-full px-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm bg-white focus:outline-none focus:border-[#007a8a]">
                                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>

                            <button onClick={handleAdd} disabled={!selectedUser || !selectedGroup || !selectedRole || submitting}
                                className="w-full mt-4 bg-[#003d45] hover:bg-[#007a8a] text-white py-3 rounded-xl font-black uppercase tracking-wider text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                {submitting ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> : "Save Assignment"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
